/**
 * API client telecom WiFi (Phase 2 — Sinergie WeFly).
 * Endpoints /v1/operations/telecom/*
 */
import { apiClient, unwrap, unwrapPaginated } from './client';
import type {
  Abonne, Hotspot, OperateurMobile,
  StatutAbonne, StatutHotspot, StatutTransaction,
  TransactionMobileMoney,
} from '@/types';
import type { Paginated } from './operations';

// ── Hotspots ───────────────────────────────────────────────────────────────

export interface HotspotListParams {
  statut?: StatutHotspot;
  ville?:  string;
  actif?:  boolean;
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface CreateHotspotPayload {
  code:                  string;
  nom:                   string;
  adresse?:              string;
  quartier?:             string;
  ville?:                string;
  latitude?:             number;
  longitude?:            number;
  statut?:               StatutHotspot;
  dateInstallation?:     string;
  derniereMaintenance?:  string;
  capaciteUtilisateurs?: number;
  fournisseurInternet?:  string;
  bandwidthMbps?:        number;
  notes?:                string;
  actif?:                boolean;
}

export type UpdateHotspotPayload = Partial<CreateHotspotPayload>;

// ── Abonnés ────────────────────────────────────────────────────────────────

export interface AbonneListParams {
  statut?:            StatutAbonne;
  hotspotPrefereId?:  string;
  search?:            string;
  page?:              number;
  limit?:             number;
}

export interface CreateAbonnePayload {
  msisdn:            string;
  nom?:              string;
  email?:            string;
  hotspotPrefereId?: string;
  planAbonnement?:   string;
  statut?:           StatutAbonne;
  soldeDataMo?:      number;
  notes?:            string;
}

export type UpdateAbonnePayload = Partial<Omit<CreateAbonnePayload, 'msisdn'>>;

// ── Transactions Mobile Money ──────────────────────────────────────────────

export interface TransactionListParams {
  statut?:    StatutTransaction;
  operateur?: OperateurMobile;
  abonneId?:  string;
  hotspotId?: string;
  dateFrom?:  string;
  dateTo?:    string;
  page?:      number;
  limit?:     number;
}

export interface CreateTransactionPayload {
  reference:        string;
  operateur:        OperateurMobile;
  msisdn:           string;
  montantCents:     number;
  dataAchetMo?:     number;
  abonneId?:        string;
  hotspotId?:       string;
  statut?:          StatutTransaction;
  dateTransaction?: string;
  notes?:           string;
}

export interface UpdateTransactionPayload {
  statut?: StatutTransaction;
  notes?:  string;
}

// ── API ────────────────────────────────────────────────────────────────────

export const telecomApi = {
  // Hotspots
  listHotspots: async (params: HotspotListParams = {}): Promise<Paginated<Hotspot>> => {
    const res = await apiClient.get('/operations/telecom/hotspots', { params });
    return unwrapPaginated<Hotspot>(res);
  },
  getHotspot: async (id: string): Promise<Hotspot> => {
    const res = await apiClient.get(`/operations/telecom/hotspots/${id}`);
    return unwrap<Hotspot>(res);
  },
  createHotspot: async (payload: CreateHotspotPayload): Promise<Hotspot> => {
    const res = await apiClient.post('/operations/telecom/hotspots', payload);
    return unwrap<Hotspot>(res);
  },
  updateHotspot: async (id: string, payload: UpdateHotspotPayload): Promise<Hotspot> => {
    const res = await apiClient.patch(`/operations/telecom/hotspots/${id}`, payload);
    return unwrap<Hotspot>(res);
  },
  deleteHotspot: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/telecom/hotspots/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // Abonnés
  listAbonnes: async (params: AbonneListParams = {}): Promise<Paginated<Abonne>> => {
    const res = await apiClient.get('/operations/telecom/abonnes', { params });
    return unwrapPaginated<Abonne>(res);
  },
  getAbonne: async (id: string): Promise<Abonne> => {
    const res = await apiClient.get(`/operations/telecom/abonnes/${id}`);
    return unwrap<Abonne>(res);
  },
  createAbonne: async (payload: CreateAbonnePayload): Promise<Abonne> => {
    const res = await apiClient.post('/operations/telecom/abonnes', payload);
    return unwrap<Abonne>(res);
  },
  updateAbonne: async (id: string, payload: UpdateAbonnePayload): Promise<Abonne> => {
    const res = await apiClient.patch(`/operations/telecom/abonnes/${id}`, payload);
    return unwrap<Abonne>(res);
  },
  deleteAbonne: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/telecom/abonnes/${id}`);
    return unwrap<{ deleted: true }>(res);
  },

  // Transactions
  listTransactions: async (params: TransactionListParams = {}): Promise<Paginated<TransactionMobileMoney>> => {
    const res = await apiClient.get('/operations/telecom/transactions', { params });
    return unwrapPaginated<TransactionMobileMoney>(res);
  },
  getTransaction: async (id: string): Promise<TransactionMobileMoney> => {
    const res = await apiClient.get(`/operations/telecom/transactions/${id}`);
    return unwrap<TransactionMobileMoney>(res);
  },
  createTransaction: async (payload: CreateTransactionPayload): Promise<TransactionMobileMoney> => {
    const res = await apiClient.post('/operations/telecom/transactions', payload);
    return unwrap<TransactionMobileMoney>(res);
  },
  updateTransaction: async (id: string, payload: UpdateTransactionPayload): Promise<TransactionMobileMoney> => {
    const res = await apiClient.patch(`/operations/telecom/transactions/${id}`, payload);
    return unwrap<TransactionMobileMoney>(res);
  },
  deleteTransaction: async (id: string): Promise<{ deleted: true }> => {
    const res = await apiClient.delete(`/operations/telecom/transactions/${id}`);
    return unwrap<{ deleted: true }>(res);
  },
};
